'use client'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { HStack, VStack } from '@chakra-ui/react'
import type { AddToCartPayload, ProductPrice } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, getProductPrice } from 'shared-utils'
import { useProductContext } from '../hooks/useProductContext'
import { IngridPDPWidget } from '../../delivery'
import ProductDetailDeliveryLabel from './ProductDetailDeliveryLabel'
import ProductDetailAddToCartButton from './ProductDetailAddToCartButton'
import ProductDetailAddToCartCounter from './ProductDetailAddToCartCounter'
import { QuantityDiscountTeaser } from './QuantityDiscountTeaser'

interface IProductDeliveryStateProps {
  onAddToCart: (payload: AddToCartPayload) => void
  isDisabled?: boolean
  totalPrices: ProductPrice
}

export interface ProductDetailAddToCartOverrides {
  updateCart?: string
  addToCart?: string
}

const ProductDetailAddToCart: FC<IProductDeliveryStateProps> = ({
  isDisabled,
  onAddToCart,
  totalPrices
}) => {
  const { product, productDelivery, cartItems, locale, shippingConfig } = useProductContext()

  const overrides = useOverridesContext<keyof ProductDetailAddToCartOverrides>()
  const shippingProvider = shippingConfig?.provider

  const itemInCart = useMemo(() => {
    return cartItems.find((item) => item.id === product.id)
  }, [cartItems, product.id])

  const [qty, setQty] = useState(itemInCart?.qty ?? 1)

  const isProductInCart = !!itemInCart

  const btnLabel = isProductInCart
    ? overrideText('Uppdatera varukorg', overrides?.updateCart)
    : overrideText('Lägg till i varukorg', overrides?.addToCart)

  const renderShippingProviderDetails = () => {
    if (!shippingProvider) {
      return null
    }

    switch (shippingProvider) {
      case 'INGRID':
        return (
          <IngridPDPWidget
            config={{
              locale: locale ?? 'sv',
              currency: shippingConfig?.ingridOptions?.purchaseCurrency ?? DEFAULT_CURRENCY,
              country: shippingConfig?.ingridOptions?.purchaseCountry ?? DEFAULT_COUNTRY
            }}
            product={product}
            quantity={qty}
            totalPrice={getProductPrice(totalPrices)}
          />
        )
      case 'DEFAULT':
      case 'POSTNORD':
      case 'NSHIFT':
        return null
    }
  }

  const isShippingProviderDetailsVisible =
    typeof shippingProvider === 'string' &&
    shippingProvider !== 'DEFAULT' &&
    productDelivery?.buyable
  const isButtonDisabled = !productDelivery?.buyable || !getProductPrice(product) || isDisabled

  return (
    <VStack alignItems="stretch" w="100%">
      {/* Above the controls, and driven by the live counter rather than by what
          is in the cart: the shopper should see the offer move as they dial the
          quantity up, before they commit to anything. */}
      <QuantityDiscountTeaser product={product} quantity={qty} surface="pdp" />

      <HStack align="flex-start" w="100%" pt={6}>
        <ProductDetailAddToCartCounter
          isDisabled={isButtonDisabled}
          onChange={(_valueAsString, valueAsNumber) => {
            setQty(valueAsNumber)
          }}
          value={qty}
        />

        <VStack align="flex-start" flex={4}>
          <ProductDetailAddToCartButton
            aria-label={btnLabel}
            isDisabled={isButtonDisabled}
            onClick={() => {
              onAddToCart({ product, qty })
            }}
          >
            {btnLabel}
          </ProductDetailAddToCartButton>

          {!isShippingProviderDetailsVisible && (
            <ProductDetailDeliveryLabel label={productDelivery?.label} />
          )}
        </VStack>
      </HStack>

      {isShippingProviderDetailsVisible && renderShippingProviderDetails()}
    </VStack>
  )
}

export default ProductDetailAddToCart
